<template>
    <Head :title="$t('apartments.notes')" />

    <div class="p-2 md:p-6">
        <div class="flex items-start justify-between gap-3 mb-6">
            <div>
                <h1 class="text-2xl font-semibold text-minplan-blue">
                    {{ $t("apartments.notes") }}
                </h1>
                <p class="text-sm text-minplan-darkbeige mt-1">
                    {{ $t("apartments.notesSubtitle") }}
                </p>
            </div>
            <primary-button v-if="!showForm" @click="openNew">
                {{ $t("apartments.newNote") }}
            </primary-button>
        </div>

        <!-- Formular: anlegen und bearbeiten -->
        <div
            v-if="showForm"
            class="bg-white border border-minplan-lightwhite rounded-lg p-4 md:p-6 mb-6"
        >
            <div class="grid grid-cols-1 md:grid-cols-12 gap-4">
                <div class="md:col-span-6">
                    <label
                        class="block text-xs uppercase tracking-wider text-minplan-darkbeige mb-1"
                    >
                        {{ $t("apartments.noteTitle") }}
                    </label>
                    <input
                        v-model="form.title"
                        type="text"
                        class="block w-full border-gray-300 rounded-md shadow-sm sm:text-sm focus:ring-minplan-blue focus:border-minplan-blue"
                        :placeholder="$t('apartments.noteTitlePlaceholder')"
                    />
                    <jet-input-error :message="form.errors.title" class="mt-1" />
                </div>
                <div class="md:col-span-3">
                    <label
                        class="block text-xs uppercase tracking-wider text-minplan-darkbeige mb-1"
                    >
                        {{ $t("apartments.noteCategory") }}
                    </label>
                    <select
                        v-model="form.category"
                        class="block w-full border-gray-300 rounded-md shadow-sm sm:text-sm focus:ring-minplan-blue focus:border-minplan-blue"
                    >
                        <option
                            v-for="category in categories"
                            :key="category"
                            :value="category"
                        >
                            {{ $t("apartments.noteCategories." + category) }}
                        </option>
                    </select>
                </div>
                <div class="md:col-span-3">
                    <label
                        class="block text-xs uppercase tracking-wider text-minplan-darkbeige mb-1"
                    >
                        {{ $t("apartments.noteDate") }}
                    </label>
                    <input
                        v-model="form.note_date"
                        type="date"
                        class="block w-full border-gray-300 rounded-md shadow-sm sm:text-sm focus:ring-minplan-blue focus:border-minplan-blue"
                    />
                    <jet-input-error :message="form.errors.note_date" class="mt-1" />
                </div>
            </div>

            <div class="mt-4">
                <label
                    class="block text-xs uppercase tracking-wider text-minplan-darkbeige mb-1"
                >
                    {{ $t("apartments.noteParticipants") }}
                </label>
                <input
                    v-model="form.participants"
                    type="text"
                    class="block w-full border-gray-300 rounded-md shadow-sm sm:text-sm focus:ring-minplan-blue focus:border-minplan-blue"
                    :placeholder="$t('apartments.noteParticipantsPlaceholder')"
                />
                <p class="mt-1 text-xs text-minplan-darkbeige">
                    {{ $t("apartments.noteParticipantsHint") }}
                </p>
            </div>

            <div class="mt-4">
                <label
                    class="block text-xs uppercase tracking-wider text-minplan-darkbeige mb-1"
                >
                    {{ $t("apartments.noteBody") }}
                </label>
                <textarea
                    v-model="form.body"
                    rows="5"
                    class="block w-full border-gray-300 rounded-md shadow-sm sm:text-sm focus:ring-minplan-blue focus:border-minplan-blue"
                    :placeholder="$t('apartments.noteBodyPlaceholder')"
                ></textarea>
            </div>

            <div class="flex flex-wrap items-center justify-end gap-3 mt-4">
                <!-- Auftrag Luca, 2026-08-17, Entscheid 10: die Freigabe
                     speichert hier MIT dem Formular. Links neben den Aktionen,
                     damit sie zum Entscheid gehört und nicht zur Eingabe. -->
                <switch-input
                    v-model="form.visible_for_buyer"
                    compact
                    class="mr-auto"
                    :label="$t('apartments.noteVisibleForBuyer')"
                />
                <button
                    type="button"
                    class="uppercase font-semibold text-xs tracking-widest"
                    @click="cancel"
                >
                    {{ $t("actions.cancel") }}
                </button>
                <primary-button :disabled="form.processing" @click="save">
                    {{ $t("actions.save") }}
                </primary-button>
            </div>
        </div>

        <!-- Einträge -->
        <div v-if="notes && notes.length">
            <template v-for="(entry, i) in notes" :key="entry.uuid">
                <p
                    v-if="monthLabel(i)"
                    class="text-xs uppercase tracking-wider text-minplan-darkbeige mt-6 mb-2"
                >
                    {{ monthLabel(i) }}
                </p>

                <div
                    class="bg-white border border-minplan-lightwhite rounded-lg mb-3 hover:border-gray-200"
                >
                    <button
                        type="button"
                        class="w-full flex items-start gap-4 p-4 text-left"
                        @click="toggle(entry.uuid)"
                    >
                        <span
                            class="flex-none w-10 h-10 rounded-lg bg-minplan-lightwhite grid place-items-center text-minplan-greybeige"
                        >
                            <component :is="iconFor(entry.category)" class="h-5 w-5" />
                        </span>

                        <span class="flex-1 min-w-0">
                            <span class="block font-semibold text-minplan-blue truncate">
                                {{ entry.title }}
                            </span>
                            <span
                                class="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-minplan-darkbeige"
                            >
                                <span>{{
                                    $t("apartments.noteCategories." + entry.category)
                                }}</span>
                                <span>{{ entry.dateFormatted }}</span>
                                <span v-if="participantCount(entry)">
                                    {{ participantCount(entry) }}
                                    {{ $t("apartments.noteParticipants") }}
                                </span>
                                <span>{{ entry.author }}</span>
                                <!-- Entscheid 13: Kennzeichen in der
                                     ZUGEKLAPPTEN Zeile — hier ist Platz für das
                                     Wort, anders als im engen Anriss-Block auf
                                     der Wohnungs-Übersicht. -->
                                <span
                                    v-if="entry.visibleForBuyer"
                                    class="inline-flex items-center gap-1 text-minplan-blue"
                                >
                                    <EyeIcon class="h-3.5 w-3.5" />
                                    {{ $t("apartments.noteVisibleBadge") }}
                                </span>
                            </span>
                        </span>

                        <ChevronDownIcon
                            class="flex-none h-5 w-5 text-gray-300 transition-transform"
                            :class="{ 'rotate-180': isOpen(entry.uuid) }"
                        />
                    </button>

                    <div v-if="isOpen(entry.uuid)" class="px-4 pb-4 pl-18">
                        <div class="border-t border-minplan-lightwhite pt-3 ml-14">
                            <p
                                v-if="entry.participants"
                                class="text-sm text-minplan-greybeige"
                            >
                                <span class="text-minplan-darkbeige">
                                    {{ $t("apartments.noteParticipants") }}:
                                </span>
                                {{ entry.participants }}
                            </p>

                            <p
                                v-if="entry.body"
                                class="text-sm text-minplan-darkgreybeige mt-2 whitespace-pre-wrap leading-relaxed"
                            >
                                {{ entry.body }}
                            </p>

                            <!-- Entscheid 10: derselbe Schalter wie im
                                 Formular, aber er speichert SOFORT über einen
                                 eigenen Endpunkt — die Notiz muss dafür nicht
                                 ins Formular. Ohne Bestätigungsdialog in beide
                                 Richtungen (Entscheid 11): der Schalter IST
                                 die bewusste Handlung. -->
                            <div class="mt-4">
                                <switch-input
                                    :model-value="Boolean(entry.visibleForBuyer)"
                                    compact
                                    :label="$t('apartments.noteVisibleForBuyer')"
                                    @update:modelValue="setVisibility(entry, $event)"
                                />
                            </div>

                            <div class="flex items-center gap-3 mt-4">
                                <span class="text-xs text-minplan-darkbeige">
                                    {{ $t("apartments.noteCreated") }}
                                    {{ entry.createdAt }}
                                    <template v-if="entry.edited">
                                        · {{ $t("apartments.noteEdited") }}</template
                                    >
                                </span>
                                <span class="ml-auto flex gap-3">
                                    <button
                                        type="button"
                                        class="text-xs text-minplan-darkbeige hover:text-minplan-blue"
                                        @click="openEdit(entry)"
                                    >
                                        {{ $t("actions.edit") }}
                                    </button>
                                    <button
                                        type="button"
                                        class="text-xs text-minplan-darkbeige hover:text-red-700"
                                        @click="remove(entry)"
                                    >
                                        {{ $t("actions.delete") }}
                                    </button>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <p
            v-else-if="!showForm"
            class="py-10 text-center text-sm text-minplan-darkgreybeige"
        >
            {{ $t("apartments.emptyNote") }}
        </p>
    </div>
</template>

<script>
import { defineComponent } from "vue";
import { Head, useForm } from "@inertiajs/inertia-vue3";
import { ChevronDownIcon, EyeIcon } from "@heroicons/vue/outline";
import { noteIconFor, NOTE_ICONS } from "@/Shared/noteIcons";
import { noteMonthLabel } from "@/Shared/noteMonths";
import ApartmentLayout from "@/Layouts/ApartmentLayout";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import JetInputError from "@/Jetstream/InputError";
import SwitchInput from "@/Shared/Components/SwitchInput";

export default defineComponent({
    props: ["apartment", "notes", "notesLink"],
    layout: ApartmentLayout,
    components: {
        Head,
        PrimaryButton,
        JetInputError,
        ChevronDownIcon,
        EyeIcon,
        SwitchInput,
    },
    data() {
        return {
            showForm: false,
            editing: null,
            open: [],
            categories: Object.keys(NOTE_ICONS),
            form: useForm({
                title: "",
                category: "note",
                note_date: new Date().toISOString().slice(0, 10),
                participants: "",
                body: "",
                // Standard bleibt intern (Entscheid 1) — eine neue Notiz ist
                // erst freigegeben, wenn der Schalter bewusst umgelegt wird.
                visible_for_buyer: false,
            }),
        };
    },
    mounted() {
        // Kommt man über "Neuer Eintrag" von der Wohnungsübersicht, ist das
        // Formular bereits offen — sonst müsste man zweimal klicken.
        if (new URLSearchParams(window.location.search).get("neu")) {
            this.openNew();
        }
    },
    methods: {
        iconFor(category) {
            return noteIconFor(category);
        },
        participantCount(entry) {
            if (!entry.participants) return 0;
            return entry.participants.split(",").filter((p) => p.trim()).length;
        },
        isOpen(uuid) {
            return this.open.includes(uuid);
        },
        toggle(uuid) {
            const i = this.open.indexOf(uuid);
            if (i === -1) this.open.push(uuid);
            else this.open.splice(i, 1);
        },
        // Herausgelöst nach `@/Shared/noteMonths`, weil die Käuferseite
        // (`Pages/Client/Apartment/Notes.vue`) dieselbe Gliederung zeigt.
        // Der frühere erste Parameter (`entry`) entfällt — die Funktion holt
        // sich den Eintrag über den Index aus der Liste.
        monthLabel(index) {
            return noteMonthLabel(this.$t.bind(this), this.notes || [], index);
        },
        openNew() {
            this.editing = null;
            this.form.reset();
            this.form.clearErrors();
            this.form.category = "note";
            this.form.note_date = new Date().toISOString().slice(0, 10);
            this.showForm = true;
        },
        openEdit(entry) {
            this.editing = entry;
            this.form.clearErrors();
            this.form.title = entry.title;
            this.form.category = entry.category || "note";
            this.form.note_date = entry.date;
            this.form.participants = entry.participants || "";
            this.form.body = entry.body || "";
            // Entscheid 12: Bearbeiten lässt die Freigabe stehen. Das Formular
            // ist mit dem Ist-Zustand vorbelegt und schreibt ihn unverändert
            // zurück, wenn niemand den Schalter anfasst.
            this.form.visible_for_buyer = Boolean(entry.visibleForBuyer);
            this.showForm = true;
        },
        cancel() {
            this.showForm = false;
            this.editing = null;
            this.form.clearErrors();
        },
        save() {
            const done = {
                preserveScroll: true,
                onSuccess: () => {
                    this.showForm = false;
                    this.editing = null;
                    this.form.reset();
                },
            };

            if (this.editing) {
                this.form.put(
                    this.route("advisors.apartments.notes.update", {
                        apartment: this.apartment.uuid,
                        note: this.editing.uuid,
                    }),
                    done
                );
            } else {
                this.form.post(
                    this.route("advisors.apartments.notes.store", {
                        apartment: this.apartment.uuid,
                    }),
                    done
                );
            }
        },
        /**
         * Der Schalter an der Notiz in der Liste (Entscheid 10): speichert
         * sofort über einen eigenen Endpunkt, die Notiz muss dafür nicht ins
         * Formular. `$inertia.patch` statt Axios (CLAUDE.md Regel 9);
         * `preserveScroll`, damit die Liste nicht an den Anfang springt.
         * Ohne Bestätigungsdialog in beide Richtungen (Entscheid 11).
         */
        setVisibility(entry, value) {
            this.$inertia.patch(
                this.route("advisors.apartments.notes.updateVisibility", {
                    apartment: this.apartment.uuid,
                    note: entry.uuid,
                }),
                { visible_for_buyer: value },
                { preserveScroll: true }
            );
        },
        remove(entry) {
            if (!confirm(this.$t("apartments.noteDeleteConfirm"))) return;
            this.$inertia.delete(
                this.route("advisors.apartments.notes.destroy", {
                    apartment: this.apartment.uuid,
                    note: entry.uuid,
                }),
                { preserveScroll: true }
            );
        },
    },
});
</script>

<style scoped></style>
